import type { RT60Method, RT60Result } from '@/types/analysis';
import type { AbsorptionSpectrum } from '@/types/material';
import type { PlacedObject } from '@/types/object';
import type { RoomDimensions, SurfaceMaterials } from '@/types/room';
import {
  roomVolume,
  rt60Rating,
  totalAbsorption,
  totalSurfaceArea,
} from './acousticHelpers';

/**
 * Sabine constant. RT60 = k·V/A where k = 0.161 s·m⁻¹ in SI (metric).
 * Imperial conversion (k = 0.049) is intentionally NOT supported — the
 * app is metric throughout.
 */
const SABINE_K = 0.161;

/** Clamp ᾱ strictly below 1 so the Eyring logarithm stays finite. */
const EYRING_ALPHA_CAP = 0.999;

export interface CalculateRT60Options {
  /** `sabine` (default) or `eyring`. */
  method?: RT60Method;
}

/**
 * Compute RT60 for a room at all 6 octave bands using the requested method.
 *
 * Both formulas share the same numerator (0.161 · V). They differ in the
 * denominator:
 *   Sabine : A
 *   Eyring : −S · ln(1 − A/S)
 *
 * When A → 0 the room is effectively a perfect reflector and the answer
 * is mathematically infinite. We return 0 in that degenerate case to
 * keep the UI from rendering `Infinity` / `NaN`; the recommendations
 * engine surfaces the problem in plain language instead.
 */
export function calculateRT60(
  dimensions: RoomDimensions,
  materials: SurfaceMaterials,
  objects: readonly PlacedObject[],
  options: CalculateRT60Options = {},
): RT60Result {
  const method = options.method ?? 'sabine';
  const V = roomVolume(dimensions);
  const S = totalSurfaceArea(dimensions);
  const A = totalAbsorption(dimensions, materials, objects);

  const perBand: number[] = [];
  const avgAlpha: number[] = [];

  for (let i = 0; i < 6; i++) {
    const Ai = A[i]!;
    const alphaBar = S > 0 ? Ai / S : 0;
    avgAlpha.push(alphaBar);

    if (Ai <= 0 || V <= 0) {
      perBand.push(0);
      continue;
    }

    if (method === 'sabine') {
      perBand.push((SABINE_K * V) / Ai);
    } else {
      const capped = Math.min(EYRING_ALPHA_CAP, alphaBar);
      const denom = -S * Math.log(1 - capped);
      perBand.push(denom > 0 ? (SABINE_K * V) / denom : 0);
    }
  }

  const perBandTuple = perBand as unknown as AbsorptionSpectrum;
  const avgAlphaTuple = avgAlpha as unknown as AbsorptionSpectrum;
  const hero = perBand[2]!; // 500 Hz
  const average = perBand.reduce((s, v) => s + v, 0) / 6;

  return {
    method,
    perBand: perBandTuple,
    hero,
    average,
    volume: V,
    totalSurfaceArea: S,
    absorptionPerBand: A,
    avgAlphaPerBand: avgAlphaTuple,
    rating: rt60Rating(hero),
  };
}

/**
 * Compute both Sabine and Eyring side-by-side. Convenient for the
 * RT60 hero display (which shows a small comparison badge).
 */
export function calculateBothMethods(
  dimensions: RoomDimensions,
  materials: SurfaceMaterials,
  objects: readonly PlacedObject[],
): { sabine: RT60Result; eyring: RT60Result } {
  return {
    sabine: calculateRT60(dimensions, materials, objects, { method: 'sabine' }),
    eyring: calculateRT60(dimensions, materials, objects, { method: 'eyring' }),
  };
}
